import React, { useState } from 'react';
import { Check, LayoutGrid, Lock } from 'lucide-react';
import { Checkbox } from 'components/forms';
import { atelierRolesAPI } from 'services/swagger-api';
import { GetReferentielBriquesResponse } from 'api/atelier-roles';
import { portailLabel } from 'core/roles';
import { InlineError, portailStyle, runOperation } from 'back-office/shared/components/rolesUi';
import { SectionHeader } from './SectionHeader';

interface PortailsTabProps {
  referentiel: GetReferentielBriquesResponse;
  onChanged: () => Promise<void>;
}

/**
 * Une carte par portail, listant les briques qui s'y expriment. Les briques
 * standard sont figées (le code fait foi) ; les personnalisées se cochent ou
 * se décochent directement dans la carte du portail.
 */
export const PortailsTab: React.FC<PortailsTabProps> = ({ referentiel, onChanged }) => {
  const [error, setError] = useState<string | null>(null);
  const [savingBrique, setSavingBrique] = useState<string | null>(null);

  const orphelines = referentiel.briques.filter((b) => b.portails.length === 0);

  const toggle = async (nom: string, portail: string) => {
    const brique = referentiel.briques.find((b) => b.nom === nom);
    if (!brique || brique.estStandard) return;
    const next = brique.portails.includes(portail)
      ? brique.portails.filter((p) => p !== portail)
      : [...brique.portails, portail];
    setSavingBrique(nom);
    const err = await runOperation(() =>
      atelierRolesAPI.updateBrique({
        nom,
        updateBriqueCommand: { description: brique.description ?? null, portails: next }
      })
    );
    setSavingBrique(null);
    setError(err);
    if (!err) await onChanged();
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={LayoutGrid}
        title="Portails"
        subtitle="Un portail n'expose que les briques qui lui sont rattachées : les droits effectifs d'un compte sont l'intersection entre ses briques et celles du portail actif. Les briques standard sont figées ; cochez une brique personnalisée pour la rattacher au portail."
      />

      {orphelines.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {orphelines.length === 1 ? 'Brique orpheline' : 'Briques orphelines'} (rattachée à aucun portail,
          donc sans effet) : <span className="font-medium">{orphelines.map((b) => b.nom).join(', ')}</span>
        </div>
      )}

      <InlineError message={error} />

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {referentiel.portails.map((portail) => {
          const nbBriques = referentiel.briques.filter((b) => b.portails.includes(portail)).length;
          const style = portailStyle(portail);
          const Icon = style.icon;
          return (
            <div key={portail} className="flex flex-col border rounded-xl bg-white shadow-sm overflow-hidden">
              <header className={`flex items-center justify-between gap-3 px-6 py-4 ${style.band}`}>
                <h3 className="flex items-center gap-2.5 text-base font-semibold text-white">
                  <Icon className="h-5 w-5 text-white/90" />
                  {portailLabel(portail)}
                </h3>
                <span className="text-xs tabular-nums text-white/80">
                  {nbBriques} {nbBriques > 1 ? 'briques' : 'brique'}
                </span>
              </header>
              <ul className="divide-y">
                {referentiel.briques.map((brique) => {
                  const active = brique.portails.includes(portail);
                  return (
                    <li key={brique.nom} className="flex items-center justify-between gap-3 px-6 py-3">
                      <span className={active ? 'text-sm font-medium text-gray-900' : 'text-sm text-gray-400'}>
                        {brique.nom}
                      </span>
                      {brique.estStandard ? (
                        active ? (
                          <span
                            className="inline-flex items-center gap-1 text-slate-500"
                            title="Défini par le code"
                          >
                            <Check className="h-4 w-4" />
                            <Lock className="h-3 w-3" />
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )
                      ) : (
                        <Checkbox
                          name={`portail-${portail}-${brique.nom}`}
                          disabled={savingBrique === brique.nom}
                          checked={active}
                          onChange={() => toggle(brique.nom, portail)}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};
